/**
 * Data Fetch Table Component
 *
 * Table displaying data fetch requests with:
 * - Unified search across fields
 * - Pagination
 * - Account details action
 * - CSV Download (legacy + job-based)
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { Table, Button, SearchBar, DatePicker } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import { dataFetchAPI } from '../../../services/data-fetch-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Toast } from '../../ui/Toast';
import type {
  DataFetchTableProps,
  DataFetchRequest,
  DataFetchCSVData,
  DateRange,
} from '../../../types/data-fetch-request';

/**
 * Filter data fetch requests based on search query and domain
 */
const filterDataFetchRequests = (
  requests: DataFetchRequest[],
  searchQuery: string,
  searchDomain: string
): DataFetchRequest[] => {
  if (!searchQuery.trim()) {
    return requests;
  }

  const query = searchQuery.toLowerCase();

  return requests.filter((request) => {
    switch (searchDomain) {
      case 'vua':
        return request.customer_vua.toLowerCase().includes(query);
      case 'consent':
        return request.consent_id.toLowerCase().includes(query);
      case 'session':
        return request.session_id.toLowerCase().includes(query);
      case 'transaction':
        return request.txn_id.toLowerCase().includes(query);
      case 'aa':
        return request.notifier_id.toLowerCase().includes(query);
      case 'all':
      default:
        // Search across all fields
        return (
          request.customer_vua.toLowerCase().includes(query) ||
          request.consent_id.toLowerCase().includes(query) ||
          request.session_id.toLowerCase().includes(query) ||
          request.txn_id.toLowerCase().includes(query) ||
          request.notifier_id.toLowerCase().includes(query) ||
          request.createdAt.toLowerCase().includes(query) ||
          request.fi_data_range_from.toLowerCase().includes(query) ||
          request.fi_data_range_to.toLowerCase().includes(query)
        );
    }
  });
};

/**
 * Calculate days difference between two dates
 */
const getDaysDifference = (startDate: Date, endDate: Date): number => {
  const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.round(timeDiff / (1000 * 3600 * 24));
};

/**
 * Generate CSV content from data fetch requests
 */
const generateCSVContent = (requests: DataFetchRequest[]): string => {
  const csvData: DataFetchCSVData[] = requests.map((request) => ({
    'Customer VUA':
      request.customer_consents_request?.customer_vua || request.customer_vua || '',
    'Consent Id': request.consent_id || '',
    'Fetch Date': request.createdAt || '',
    'FI Data Range From': request.fi_data_range_from || '',
    'FI Data Range To': request.fi_data_range_to || '',
    'Session Id': request.session_id || '',
    'AA Id': request.notifier_id || '',
    'Transaction Id': request.txn_id || '',
    'Account Details': JSON.stringify(
      (request.accounts || []).map((a) => ({
        fi_type: a.fi_type || '',
        fip_id: a.fip_id || '',
        account_type: a.account_type || '',
        link_reference_number: a.link_reference_number || '',
        masked_account_number: a.masked_account_number || '',
      }))
    ),
  }));

  if (csvData.length === 0) {
    return '';
  }

  const headers = Object.keys(csvData[0]);
  const csvEscape = (val: any) => {
    const s = val === null || val === undefined ? '' : String(val);
    return '"' + s.replace(/"/g, '""') + '"';
  };
  const csvString = [
    headers.join(','),
    ...csvData.map((row) =>
      headers
        .map((fieldName) => csvEscape(row[fieldName as keyof DataFetchCSVData]))
        .join(',')
    ),
  ].join('\r\n');

  return csvString;
};

/**
 * Trigger browser download of CSV file
 */
const downloadCSVFile = (csvContent: string, filename: string = 'data_fetch_request_list.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const downloadURL = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadURL;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(downloadURL);
};

export const DataFetchTable: React.FC<DataFetchTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onViewAccountDetails,
  isLoading,
  hasAccountDetailsPermission,
  canDownloadCSV,
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState('all');

  // Date picker modal state
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });
  const [dateError, setDateError] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Error handling
  const { handleError } = useErrorHandler();

  // Feature flag
  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';

  // Search bar domains
  const domains = [
    { value: 'all', label: 'All Fields' },
    { value: 'vua', label: 'Customer VUA' },
    { value: 'consent', label: 'Consent ID' },
    { value: 'session', label: 'Session ID' },
    { value: 'transaction', label: 'Transaction ID' },
    { value: 'aa', label: 'AA ID' },
  ];

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    return filterDataFetchRequests(data, searchQuery, searchDomain);
  }, [data, searchQuery, searchDomain]);

  /**
   * Handle download CSV button click - show date picker modal
   */
  const handleDownloadClick = () => {
    setShowDateModal(true);
    setDateError('');
  };

  /**
   * Handle cancel date picker modal
   */
  const handleCancelDateModal = useCallback(() => {
    setShowDateModal(false);
    setDateError('');
    setDateRange({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
    });
  }, []);

  /**
   * Format date to YYYY-MM-DD for job-based download API
   */
  const formatDateISO = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  /**
   * Format date to YYYY-MM-DD for legacy CSV download API
   * Uses ISO format for unambiguous moment.js parsing on backend
   */
  const formatDateLegacy = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  /**
   * Job-based download: Create a download job via download-service
   */
  const handleJobBasedDownload = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      await downloadAPI.createJob({
        download_type: 'DATA_FETCH_REQUEST',
        file_type: 'EXCEL',
        filters: {
          from: formatDateISO(startDate),
          to: formatDateISO(endDate),
        },
      });
      setShowDateModal(false);
      setToastMessage('Report queued! Check the Download Centre');
    } catch (err) {
      handleError(err, { component: 'DataFetchTable', action: 'createDownloadJob' });
      setDateError('Failed to create download job. Please try again.');
    }
  }, [handleError]);

  /**
   * Legacy download: Download CSV directly in-browser
   */
  const handleDirectCSVDownload = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      const response = await dataFetchAPI.downloadDataFetchRequests({
        from: formatDateLegacy(startDate),
        to: formatDateLegacy(endDate),
        all: true,
      });

      const csvRows = response.data.rows || [];

      if (csvRows.length === 0) {
        setDateError('No data found for the selected date range');
        return;
      }

      const csvContent = generateCSVContent(csvRows);
      downloadCSVFile(csvContent);
      handleCancelDateModal();
    } catch (err) {
      handleError(err, { component: 'DataFetchTable', action: 'downloadCSV' });
      setDateError('Failed to download CSV. Please try again.');
    }
  }, [handleError, handleCancelDateModal]);

  /**
   * Handle download CSV with selected date range
   */
  const handleDownloadCSV = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setDateError('Please select both start and end dates');
      return;
    }

    const daysDiff = getDaysDifference(dateRange.startDate, dateRange.endDate);
    if (daysDiff > 31) {
      setDateError('Please select range between 31 days');
      return;
    }

    setIsDownloading(true);
    setDateError('');

    try {
      if (isJobBasedDownloadEnabled) {
        await handleJobBasedDownload(dateRange.startDate, dateRange.endDate);
      } else {
        await handleDirectCSVDownload(dateRange.startDate, dateRange.endDate);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Define columns
  const columns = useMemo<TableColumn<DataFetchRequest>[]>(() => {
    const cols: TableColumn<DataFetchRequest>[] = [];

    // Account Details column (conditional)
    if (hasAccountDetailsPermission) {
      cols.push({
        key: 'actions',
        header: 'Customer Accounts',
        width: 180,
        locked: true,
        render: (_value, row) => (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onViewAccountDetails(row)}
          >
            Account Details
          </Button>
        ),
      });
    }

    // Data columns
    cols.push(
      {
        key: 'customer_vua',
        header: 'Customer VUA',
        width: 200,
        render: (_value, row) => (
          <span className="text-sm">{row.customer_vua}</span>
        ),
      },
      {
        key: 'consent_id',
        header: 'Consent Id',
        width: 300,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.consent_id}</span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Fetch Date',
        width: 200,
        render: (_value, row) => (
          <span className="text-sm">{row.createdAt}</span>
        ),
      },
      {
        key: 'fi_data_range_from',
        header: 'FI Data Range From',
        width: 180,
        render: (_value, row) => (
          <span className="text-sm">{row.fi_data_range_from}</span>
        ),
      },
      {
        key: 'fi_data_range_to',
        header: 'FI Data Range To',
        width: 180,
        render: (_value, row) => (
          <span className="text-sm">{row.fi_data_range_to}</span>
        ),
      },
      {
        key: 'session_id',
        header: 'Session Id',
        width: 400,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.session_id}</span>
        ),
      },
      {
        key: 'notifier_id',
        header: 'AA Id',
        width: 150,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.notifier_id}</span>
        ),
      },
      {
        key: 'txn_id',
        header: 'Transaction Id',
        width: 300,
        render: (_value, row) => (
          <span className="text-sm font-mono">{row.txn_id}</span>
        ),
      }
    );

    return cols;
  }, [hasAccountDetailsPermission, onViewAccountDetails]);

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
        {/* Header with Search and Actions */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <SearchBar
              variant="with-dropdown"
              domains={domains}
              value={searchQuery}
              onChange={setSearchQuery}
              selectedDomain={searchDomain}
              onDomainChange={setSearchDomain}
              placeholder="Search data fetch requests..."
            />
          </div>
          {canDownloadCSV && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={handleDownloadClick}
                disabled={isDownloading}
              >
                <Download className="w-6 h-6" />
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <Table
            className="flex-1 min-h-0 shadow-sm rounded-xl"
            columns={columns}
            data={filteredData}
            loading={isLoading}
            columnManager={{
              enabled: true,
              triggerLabel: 'Manage columns',
              allowHiding: true,
            }}
            pagination={{
              currentPage,
              pageSize,
              totalItems,
              onPageChange,
              onPageSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          variant="success"
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* Date Range Picker Modal */}
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {isJobBasedDownloadEnabled ? 'Download Report' : 'Download CSV'}
              </h3>
              <button
                onClick={handleCancelDateModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isDownloading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Date Range Pickers */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Date
                    </label>
                    <DatePicker
                      selectedDate={dateRange.startDate || undefined}
                      onChange={(date) => {
                        setDateRange({ ...dateRange, startDate: date || null });
                        setDateError('');
                      }}
                      placeholder="Select start date"
                      maxDate={dateRange.endDate || new Date()}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Date
                    </label>
                    <DatePicker
                      selectedDate={dateRange.endDate || undefined}
                      onChange={(date) => {
                        setDateRange({ ...dateRange, endDate: date || null });
                        setDateError('');
                      }}
                      placeholder="Select end date"
                      minDate={dateRange.startDate || undefined}
                      maxDate={new Date()}
                    />
                  </div>
                </div>

                {/* Info Message */}
                {!isJobBasedDownloadEnabled && (
                  <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-md">
                    Maximum 10000 rows data will be able to download in CSV file.
                  </div>
                )}

                {/* Error Message */}
                {dateError && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    {dateError}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <Button
                variant="secondary"
                onClick={handleCancelDateModal}
                disabled={isDownloading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadCSV}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
